import { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaChevronDown, FaChevronRight, FaGift, FaHome, FaSlidersH } from 'react-icons/fa'
import { MdLogout } from 'react-icons/md'

import { AuthContext } from '../../contexts/AuthContext'
import { buildBreadcrumbs, HOME_PATH } from './breadcrumbRoutes'
import UserPreferencesDialog from './UserPreferencesDialog'
import WhatsNewDialog from './WhatsNewDialog'
import { getReleases, getUnreadReleases, markReleasesSeen } from '../../helpers/whatsNew'
import AppSearch from './AppSearch'

/**
 * The portal's top bar. Does everything the old top bar did - the breadcrumb trail, What's New
 * (including opening by itself after an update), and preferences from the
 * user's name - with the user's name now in one place only: the account menu
 * here, which also holds log out. The old top bar's "Available" label was a
 * fixed piece of text with nothing behind it, so it isn't carried over.
 */

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?'

const readEmail = () => {
  try {
    return window.localStorage.getItem('userEmail') || ''
  } catch {
    return ''
  }
}

const AppHeader = ({ userName }: { userName: string }) => {
  const auth = useContext(AuthContext)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [menuOpen, setMenuOpen] = useState(false)
  const [preferencesVisible, setPreferencesVisible] = useState(false)

  const [whatsNewVisible, setWhatsNewVisible] = useState(false)
  const [whatsNewUnread, setWhatsNewUnread] = useState(false)
  const [whatsNewReleases, setWhatsNewReleases] = useState(getReleases)
  const [whatsNewAutomatic, setWhatsNewAutomatic] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Anything unread since the last visit opens by
  // itself, showing just what's new.
  useEffect(() => {
    const unread = getUnreadReleases()
    if (unread.length === 0) return

    setWhatsNewReleases(unread)
    setWhatsNewUnread(true)
    setWhatsNewAutomatic(true)
    setWhatsNewVisible(true)
  }, [])

  // Close the account menu on a click elsewhere or Escape.
  useEffect(() => {
    if (!menuOpen) return

    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  // Focus the first entry when the menu opens, for keyboard users.
  useEffect(() => {
    if (menuOpen) menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
  }, [menuOpen])

  const openWhatsNew = () => {
    setMenuOpen(false)
    setWhatsNewReleases(getReleases())
    setWhatsNewAutomatic(false)
    setWhatsNewVisible(true)
  }

  const closeWhatsNew = () => {
    // Closing counts as read however it was opened.
    markReleasesSeen()
    setWhatsNewUnread(false)
    setWhatsNewVisible(false)
  }

  const openPreferences = () => {
    setMenuOpen(false)
    setPreferencesVisible(true)
  }

  const logout = () => {
    setMenuOpen(false)
    window.localStorage.clear()
    auth?.setLoggedIn(false)
    navigate('/')
  }

  // Up/down arrows move through the menu.
  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])
    const at = items.indexOf(document.activeElement as HTMLButtonElement)
    const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length
    items[next]?.focus()
  }

  const onHome = pathname === HOME_PATH
  const crumbs = onHome ? [] : buildBreadcrumbs(pathname)
  const email = readEmail()

  return (
    <header className='phd'>
      <nav className='phd-crumbs' aria-label='Breadcrumb'>
        <button
          type='button'
          className={`phd-crumbs__home${onHome ? ' phd-crumbs__home--current' : ''}`}
          onClick={() => navigate(HOME_PATH)}
          aria-label='Home'
          aria-current={onHome ? 'page' : undefined}
        >
          <FaHome aria-hidden='true' />
          {onHome && <span>Home</span>}
        </button>
        {crumbs.map((crumb, index) => {
          const current = index === crumbs.length - 1
          return (
            <span key={crumb.path} className='phd-crumbs__item'>
              <FaChevronRight className='phd-crumbs__sep' aria-hidden='true' />
              <button
                type='button'
                className={`phd-crumbs__link${current ? ' phd-crumbs__link--current' : ''}`}
                aria-current={current ? 'page' : undefined}
                onClick={() => navigate(crumb.path)}
              >
                {crumb.label}
              </button>
            </span>
          )
        })}
      </nav>

      <div className='phd-actions'>
        <AppSearch />

        <button
          type='button'
          className='phd-icon'
          onClick={openWhatsNew}
          aria-label={whatsNewUnread ? "What's New - unread updates" : "What's New"}
          title="What's New"
        >
          <FaGift aria-hidden='true' />
          {whatsNewUnread && <span className='phd-icon__dot' aria-hidden='true' />}
        </button>

        <span className='phd-divider' aria-hidden='true' />

        <div className='phd-account'>
          <button
            ref={triggerRef}
            type='button'
            className={`phd-account__trigger${menuOpen ? ' phd-account__trigger--open' : ''}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup='menu'
            aria-expanded={menuOpen}
          >
            <span className='phd-avatar' aria-hidden='true'>
              {initials(userName)}
            </span>
            <span className='phd-account__name'>{userName}</span>
            <FaChevronDown className='phd-account__chevron' aria-hidden='true' />
          </button>

          {menuOpen && (
            <div ref={menuRef} className='phd-menu' role='menu' aria-label='Account' onKeyDown={onMenuKey}>
              <div className='phd-menu__who'>
                <span className='phd-avatar phd-avatar--lg' aria-hidden='true'>
                  {initials(userName)}
                </span>
                <span className='phd-menu__who-text'>
                  <span className='phd-menu__name'>{userName}</span>
                  {email && <span className='phd-menu__email'>{email}</span>}
                </span>
              </div>

              <div className='phd-menu__group'>
                <button type='button' role='menuitem' className='phd-menu__item' onClick={openPreferences}>
                  <FaSlidersH aria-hidden='true' />
                  Preferences
                </button>
                <button type='button' role='menuitem' className='phd-menu__item' onClick={openWhatsNew}>
                  <FaGift aria-hidden='true' />
                  What's New
                  {whatsNewUnread && <span className='phd-menu__pill'>New</span>}
                </button>
              </div>

              <div className='phd-menu__group'>
                <button type='button' role='menuitem' className='phd-menu__item phd-menu__item--danger' onClick={logout}>
                  <MdLogout aria-hidden='true' />
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {preferencesVisible && <UserPreferencesDialog onClose={() => setPreferencesVisible(false)} />}

      {whatsNewVisible && (
        <WhatsNewDialog releases={whatsNewReleases} openedAutomatically={whatsNewAutomatic} onClose={closeWhatsNew} />
      )}
    </header>
  )
}

export default AppHeader
